import * as React from 'react';
import { darken, lighten, rgbToHex, createTheme, ThemeProvider, useTheme, alpha } from '@mui/material';
import AppService, { alerttype, textsize } from '../services/app.service';

//https://imagecolorpicker.com/ --get color code
//https://uicolors.app/ --ui coloring recomendation

interface Props {
  id?: string;
  theme?: any,
  children?: React.ReactNode;
}

const variant: any = {
  // Original state:
  // h1=96, h2=60, h3=48, h4=34, h5=24, h6=20, 
  // subtitle1=16, subtitle2=14, body1=16, body2=14, caption=12, overline=10
  h1: {fontSize: 60},
  h2: {fontSize: 48},
  h3: {fontSize: 34},
  h4: {fontSize: 24},
  h5: {fontSize: 18},
  h6: {fontSize: 14},
  subtitle1: {fontSize: 12},
  subtitle2: {fontSize: 10},
  body1: {fontSize: 12},
  body2: {fontSize: 10},
  caption: {fontSize: 8},
  overline: {fontSize: 8},
  
}

export const ThemeDefault = {
  color: {primary: '#34b1fd', secondary: '#34b1fd', info: '#00a2e8', success: '#aeea00', warning: '#ffc90e', error: '#ff0000'},    
  font: ''
}


export const ThemeFont = [
  { name: 'roboto', caption: 'Roboto' },
  { name: 'montserrat', caption: 'Montserrat' },
  { name: 'segoe ui', caption: 'Segoe UI' },
  { name: 'helvetica neue', caption: 'Helvetica Neue' },
]

export const ThemeAlert = (type?: alerttype, opacity?: number) => {
  const palette = useTheme().palette;
  const themeAlerts = {
    default: {bgColor: palette.background.default, color: palette.text.primary, icon: ''},
    black: {bgColor: palette.common.black, color: palette.common.white, icon: ''},
    white: {bgColor: palette.common.white, color: palette.common.black, icon: ''},
    primary: {bgColor: palette.primary.light, color: palette.common.white, icon: ''}, 
    secondary: {bgColor: palette.secondary.main, color: palette.text.primary, icon: ''}, 
    success: {bgColor: palette.success.main, color: palette.text.primary, icon: 'check_circle_outline'}, 
    info: {bgColor: palette.info.main, color: palette.common.white, icon: 'info_outline'}, 
    warning: {bgColor: palette.warning.main, color: palette.text.primary, icon: 'warning_amber'}, 
    error: {bgColor: palette.error.main, color: palette.common.white, icon: 'error_outline'}
  }  

  var themeAlert = themeAlerts[type || 'default'];
  if (opacity)
    themeAlert = {...themeAlert, bgColor: alpha(themeAlert.bgColor, opacity) }
  return (themeAlert);
}

export const ThemeTextSize = (size?: textsize) => {
  const sizes = {'supersmall': 'caption', 'extrasmall': 'body2', 'small': 'subtitle1', 'medium': 'h6', 'large': 'h5', 'extralarge': 'h4', 'superlarge': 'h3', 'superextralarge': 'h2', 'maxlarge': 'h1'}
  const sizeName = sizes[size || (AppService.isMobile()? 'large' : 'medium')];
  const fontSize = variant[sizeName].fontSize;
  return ({name: sizeName, fontSize: fontSize, iconSize: fontSize * 1.5}); 
}

export const Themes: React.FunctionComponent<Props> = (props) => {
  const getTheme = () => {
    const themeDefault = AppService.mergeArray(ThemeDefault, props.theme);
    const getPalette = (color: string) => {
      var colors: any = {} 
      colors[500] = color;
      for (let j = 1; j < 10; j++) {
        colors[(10-j)*50] = rgbToHex(lighten(color, j*0.1));
        colors[500+j*50] = rgbToHex(darken(color, j*0.1));
      }
      return colors;
    }
    const palette = {primary: getPalette(themeDefault.color.primary), secondary: getPalette(themeDefault.color.secondary), info: getPalette(themeDefault.color.info), success: getPalette(themeDefault.color.success), warning: getPalette(themeDefault.color.warning), error: getPalette(themeDefault.color.error)}
    
    return createTheme({
      palette: {
        background: {
          default: palette.primary['50'],
          paper: palette.primary['100'],
        },
        text: {
          primary: palette.primary['800'],
          secondary: palette.primary['600'],
        },
        primary: {
          ...palette.primary,
          light: palette.primary['300'],
          main: palette.primary['500'],
          dark: palette.primary['700'],
          contrastText: palette.primary['900'],
        },
        secondary: {
          ...palette.secondary,
          light: palette.secondary['300'],
          main: palette.secondary['500'],
          dark: palette.secondary['700'],
          contrastText: palette.secondary['900'],
        },
        info: {
          ...palette.info,
          light: palette.info['300'],
          main: palette.info['500'],
          dark: palette.info['700'],
          contrastText: palette.info['900'],
        },
        success: {
          ...palette.success,
          light: palette.success['300'],
          main: palette.success['500'],
          dark: palette.success['700'],
          contrastText: palette.success['900'],
        },
        warning: {
          ...palette.warning,
          light: palette.warning['300'],
          main: palette.warning['500'],
          dark: palette.warning['700'],
          contrastText: palette.warning['900'],
        },
        error: {
          ...palette.error,
          light: palette.error['300'],
          main: palette.error['500'],
          dark: palette.error['700'],
          contrastText: palette.error['900'],
        },
      },
      typography: {...variant, fontFamily: props.theme?.font},
    });
  }

  const theme = getTheme();
  return (
    <ThemeProvider theme={theme}> 
      {props.children}
    </ThemeProvider>
  )
}